import type { Locale } from './types';

/**
 * Tiny inline dictionary — keeps bundle size small and avoids shipping a
 * full i18n runtime. Add keys sparingly.
 */
const DICT: Record<Locale, Record<string, string>> = {
  en: {
    'widget.open': 'Open help',
    'widget.close': 'Close',
    'widget.dismiss': 'Hide until reload',
    'widget.soundOn': 'Turn notification sound on',
    'widget.soundOff': 'Turn notification sound off',
    'welcome.close': 'Close welcome message',
    'widget.greeting': 'Hi! How can we help?',
    'widget.subtitle': 'We reply in minutes',
    'widget.welcomeTitle': 'Hi 👋',
    'widget.welcomePrompt': 'What can we help you with?',
    'widget.startChat': 'Start a conversation',
    'widget.requestHuman': 'Talk to a human',
    'widget.poweredBy': 'Powered by HelpDesk',
    'widget.poweredByLinkAria': 'Powered by HelpDesk, opens in a new tab',
    'faq.search': 'Search articles…',
    'faq.empty': 'No results.',
    'chat.start': 'Start a conversation',
    'chat.placeholder': 'Type your message…',
    'chat.send': 'Send',
    'chat.attach': 'Attach file',
    'chat.request_human': 'Talk to a human',
    'chat.connecting': "We're connecting you with an agent…",
    'chat.agent_joined': '{name} joined the chat.',
    'chat.typing': 'Typing…',
    'chat.enter_to_send': '⏎ to send',
    'error.generic': 'Something went wrong. Please try again.',
    'error.attachment_too_big': 'File is too large (max {mb}MB).',
    'error.handoff_failed': "Couldn't pass you to a human. Please try again.",
    'block.title': 'Service unavailable',
    'block.body': "We can't take new conversations right now. Please try again later.",
    'handoff.title': 'Before we hand you off to a human, tell us:',
    'handoff.submit': 'Pass me to a human',
    'handoff.cancel': 'Cancel',
    'handoff.name': 'Your name',
    'handoff.email': 'Your email',
    'handoff.business': 'Your business',
    'handoff.phone': 'Your phone',
  },
  es: {
    'widget.open': 'Abrir ayuda',
    'widget.close': 'Cerrar',
    'widget.dismiss': 'Ocultar hasta recargar',
    'widget.soundOn': 'Activar sonido de aviso',
    'widget.soundOff': 'Silenciar sonido de aviso',
    'welcome.close': 'Cerrar mensaje de bienvenida',
    'widget.greeting': 'Hola, ¿te ayudamos?',
    'widget.subtitle': 'Te respondemos en minutos',
    'widget.welcomeTitle': 'Hola 👋',
    'widget.welcomePrompt': '¿Con qué podemos ayudarte?',
    'widget.startChat': 'Iniciar conversación',
    'widget.requestHuman': 'Hablar con una persona',
    'widget.poweredBy': 'Impulsado por HelpDesk',
    'widget.poweredByLinkAria': 'Impulsado por HelpDesk, abre en una pestaña nueva',
    'faq.search': 'Buscar artículos…',
    'faq.empty': 'Sin resultados.',
    'chat.start': 'Iniciar conversación',
    'chat.placeholder': 'Escribe tu mensaje…',
    'chat.send': 'Enviar',
    'chat.attach': 'Adjuntar archivo',
    'chat.request_human': 'Hablar con una persona',
    'chat.connecting': 'Te estamos conectando con un agente…',
    'chat.agent_joined': '{name} se unió al chat.',
    'chat.typing': 'Escribiendo…',
    'chat.enter_to_send': '⏎ para enviar',
    'error.generic': 'Algo salió mal. Inténtalo de nuevo.',
    'error.attachment_too_big': 'El archivo es demasiado grande (máx {mb}MB).',
    'error.handoff_failed': 'No pudimos pasarte con un humano. Inténtalo de nuevo.',
    'block.title': 'Servicio no disponible',
    'block.body': 'No podemos atender nuevas conversaciones en este momento. Inténtalo más tarde.',
    'handoff.title': 'Antes de pasarte con un humano, compártenos:',
    'handoff.submit': 'Pasar a un humano',
    'handoff.cancel': 'Cancelar',
    'handoff.name': 'Tu nombre',
    'handoff.email': 'Tu correo',
    'handoff.business': 'Tu negocio',
    'handoff.phone': 'Tu teléfono',
  },
};

export function t(locale: Locale, key: string, params: Record<string, string | number> = {}): string {
  const table = DICT[locale] ?? DICT.en;
  const template = table[key] ?? DICT.en[key] ?? key;
  return template.replace(/\{(\w+)\}/g, (_, k: string) =>
    params[k] === undefined ? `{${k}}` : String(params[k]),
  );
}
